// Once a minute while the tab is visible and the person is active or a
// video is playing (spec §5.4), plus one on the way back to a backgrounded
// tab — never more often than that. Failures are ignored, bar the tiles they
// were carrying; the next minute retries.
import { useContext, useEffect, useRef } from "react";
import { useApiClient } from "../api/ApiClientContext";
import { tileMeter } from "../map/tileMeter";
import { isPlaying } from "./playing";
import { PresenceContext } from "./PresenceContext";

export const BEAT_MS = 60_000;
const ACTIVE_WINDOW_MS = 60_000;
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

export function usePresenceHeartbeat(): void {
  const client = useApiClient();
  const ctx = useContext(PresenceContext);
  const lastInput = useRef(Date.now());
  // The context value changes on every beat (it holds the counts), so the
  // beating effect reads it through a ref: depending on it directly would
  // clear and restart the interval on each answer, and any host that
  // re-renders more than once a minute would then never beat at all.
  const ctxRef = useRef(ctx);
  useEffect(() => {
    ctxRef.current = ctx;
  }, [ctx]);
  /** When the last beat went out, so nothing beats twice in a minute. */
  const lastBeat = useRef(0);
  /** Tiles a failed beat was carrying: they ride along with the next one
   *  instead of vanishing, so the usage tally stays honest across a flat spot. */
  const pendingTiles = useRef(0);

  useEffect(() => {
    const mark = (): void => {
      lastInput.current = Date.now();
    };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, mark, { passive: true });
    const beat = async (): Promise<void> => {
      if (document.visibilityState !== "visible") return;
      if (!navigator.onLine) return;
      // A tab switched back and forth, or an interval tick landing on top of
      // that, must not spend a second beat: one a minute is the contract.
      if (Date.now() - lastBeat.current < BEAT_MS) return;
      const active = Date.now() - lastInput.current < ACTIVE_WINDOW_MS || isPlaying();
      if (!active) return;
      lastBeat.current = Date.now();
      const tiles = pendingTiles.current + tileMeter.drain();
      try {
        const counts = await client.heartbeat({
          playing: isPlaying(), tiles, tileGuard: tileMeter.tripped(),
        });
        pendingTiles.current = 0;
        ctxRef.current?.setCounts(counts);
      } catch {
        // ignored: 401 and 503 already routed through the client's handlers.
        // The tiles are not: they go out again with the next beat.
        pendingTiles.current = tiles;
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      // Coming back to the tab is presence in itself. Without this stamp a tab
      // parked in the background for an hour would look idle on its return —
      // no input event fires on the switch — and hold no seat until the first
      // click, which is exactly when the person is back at the keyboard.
      lastInput.current = Date.now();
      void beat();
    };
    document.addEventListener("visibilitychange", onVisible);
    const id = window.setInterval(() => {
      void beat();
    }, BEAT_MS);
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, mark);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(id);
    };
  }, [client]);
}
